import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useBranding } from "../../context/BrandingContext";
import { LaundryMark } from "../LaundryMark";
import { BrandLogo } from "../BrandLogo";
import { IronScene } from "./IronScene";
import { SteamCanvas, type SteamMode } from "./SteamCanvas";

/**
 * The cinematic sign-in entrance: iron → steam → mark reveal → login card.
 *
 * Phase timings are the brief's, verbatim:
 *
 *   scene   0– 800ms   dark stage, iron on the pressing table, soft steam
 *   gather  800–1500ms  steam densifies and drifts centre; the iron dissolves
 *   reveal 1500–2200ms  the mark emerges through the steam with a gold glow
 *   settle 2200–3000ms  the card arrives with the interface's own motion
 *
 * Three exits from the cinema, all landing on the identical final layout:
 *
 *   • reduced motion — the sequence never starts. Final state, no canvas, no
 *     iron. This is a hard skip, not a faster version: vestibular safety is
 *     not a duration preference.
 *   • already seen this session — one full performance per session. A person
 *     who signs out and back in gets the finished screen with a plain fade.
 *   • any key or tap — the person asked for the form; give it to them now.
 *
 * The stage is aria-hidden throughout. To assistive technology this page is a
 * login form that happens to appear; the theatre is strictly visual.
 */

type Phase = "scene" | "gather" | "reveal" | "settle";

const STEAM_FOR_PHASE: Record<Phase, SteamMode> = {
  scene: "iron",
  gather: "gather",
  reveal: "reveal",
  settle: "idle",
};

const SEEN_KEY = "lp:entrance-played";

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function alreadyPlayed(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false; // storage blocked → play; the cinema is the default
  }
}

function markPlayed(): void {
  try {
    sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* storage blocked — nothing to record */
  }
}

export function LoginEntrance({
  children,
  onSettled,
}: {
  /** The login card. Kept out of the document's interactive order until it lands. */
  children: ReactNode;
  /** Fires once the card is in place and safe to focus. */
  onSettled?: () => void;
}) {
  const { appName, branding, language } = useBranding();
  const ar = language === "ar";

  // Whether to PLAY the cinema is decided once at mount — restarting a
  // performance mid-flight would be worse than finishing it.
  const skipCinema = useMemo(() => prefersReducedMotion() || alreadyPlayed(), []);

  const [phase, setPhase] = useState<Phase>(skipCinema ? "settle" : "scene");

  // Whether motion is ALLOWED is live, unlike the decision above. Someone who
  // turns reduced-motion on while this screen is open is asking for the motion
  // to stop now, and the CSS kill switch alone would not stop the canvas —
  // it draws its own frames and no media query reaches it.
  const [reduced, setReduced] = useState(prefersReducedMotion);

  /**
   * Ambient motion after the card lands: a faint steam wisp and the mark's
   * idle float.
   *
   * It stops on its own a few seconds later. WCAG 2.2.2 requires that motion
   * which starts automatically, runs past five seconds and sits alongside
   * other content can be stopped — and a login form the user is reading is
   * exactly "other content". Auto-stopping satisfies that without asking them
   * to find a pause control, and it makes the canvas's `off` mode a real state
   * rather than an unreachable branch: the loop exits and the screen goes
   * genuinely still instead of burning a rAF forever behind a form.
   */
  const [ambient, setAmbient] = useState(true);

  const settledRef = useRef(false);
  /** The iron's wrapper — the steam reads its position to place the plume. */
  const ironRef = useRef<HTMLDivElement | null>(null);
  const timersRef = useRef<number[]>([]);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const settle = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    for (const t of timersRef.current) window.clearTimeout(t);
    markPlayed();
    setPhase("settle");
    // Let the card's 600ms arrival finish before anything grabs focus, so the
    // focus ring does not pop onto a moving target. Tracked in timersRef so
    // the unmount cleanup cancels it — the entrance can unmount inside this
    // window when a recovered session redirects to the dashboard, and firing a
    // parent callback against an unmounted tree would violate the contract
    // even though today's caller degrades to a no-op.
    timersRef.current.push(window.setTimeout(() => onSettledRef.current?.(), 640));
    // Ambient motion gets a short grace period after the card lands, then the
    // screen goes still. See the `ambient` state above.
    timersRef.current.push(window.setTimeout(() => setAmbient(false), 4200));
  };

  useEffect(() => {
    if (skipCinema) {
      settledRef.current = true;
      markPlayed();
      // This path never calls settle(), so it must arm the ambient stop-timer
      // itself. Without it the mark floats and glows forever on every repeat
      // visit — the same WCAG 2.2.2 problem the timed path already avoids, and
      // the repeat visit is the COMMON case, so it would be the one users
      // actually live with.
      const timers = [
        window.setTimeout(() => onSettledRef.current?.(), 60),
        window.setTimeout(() => setAmbient(false), 4200),
      ];
      return () => timers.forEach(window.clearTimeout);
    }

    timersRef.current = [
      window.setTimeout(() => setPhase("gather"), 800),
      window.setTimeout(() => setPhase("reveal"), 1500),
      window.setTimeout(settle, 2200),
    ];

    // The person asked for the form — any input ends the show immediately.
    const skip = () => settle();
    window.addEventListener("pointerdown", skip);
    window.addEventListener("keydown", skip);

    return () => {
      for (const t of timersRef.current) window.clearTimeout(t);
      window.removeEventListener("pointerdown", skip);
      window.removeEventListener("keydown", skip);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The skip listeners stay attached until unmount. That is deliberate: they
  // neither preventDefault nor stopPropagation, and settle() no-ops after the
  // first call, so post-settle they are inert — removing them mid-flight would
  // buy nothing and cost a second code path.

  // Honour a reduced-motion preference turned on WHILE this screen is open.
  // The global CSS kill switch handles declarative animation live, but the
  // canvas paints its own frames, so it needs to be told.
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (event: MediaQueryListEvent) => {
      setReduced(event.matches);
      if (event.matches) settle(); // end the performance immediately
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preReveal = phase === "scene" || phase === "gather";
  const settled = phase === "settle";

  const tagline =
    (ar ? branding.loginHeadlineAr : branding.loginHeadlineEn) ??
    (ar ? "إدارة مغاسل ذكية" : "Smart Laundry Management");

  // Wide tracking and uppercase are LATIN typography. The gate requires the
  // name to contain Latin AND no Arabic at all: a bilingual tenant name like
  // "مغاسل الجوهرة Pro" must render plain, because letter-spacing severs the
  // joins of connected Arabic script. \p{Script=Arabic} covers every Arabic
  // block including the presentation forms.
  const latinName = /[a-z]/i.test(appName) && !/\p{Script=Arabic}/u.test(appName);

  return (
    <div className="lp-entrance-stage relative min-h-dvh overflow-hidden">
      {/* ── The theatre: decorative, mirrored to face the reading direction ── */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 rtl:-scale-x-100">
        {!reduced && (
          <SteamCanvas
            // Once the ambient grace period lapses the canvas is told to stop
            // spawning; its loop then exits after the last particle fades, so
            // the settled screen costs nothing.
            mode={settled && !ambient ? "off" : STEAM_FOR_PHASE[phase]}
            // So the plume starts at the iron's vents wherever the viewport
            // puts them, rather than at a fixed fraction of the stage.
            ironRef={ironRef}
            className="absolute inset-0 h-full w-full"
          />
        )}

        {/* The iron sits low in the frame and dissolves during the gather. */}
        {!skipCinema && (
          <div
            ref={ironRef}
            className={`absolute inset-x-0 bottom-[6dvh] mx-auto w-[min(88vw,560px)] sm:bottom-[8dvh]
              ${phase === "scene" ? "lp-iron-in" : "lp-iron-fade"}`}
          >
            <IronScene className="h-auto w-full" />
          </div>
        )}
      </div>

      {/* Vignette above the theatre, below the content. */}
      <div aria-hidden="true" className="lp-entrance-vignette pointer-events-none absolute inset-0" />

      {/* ── The caption of the first act ───────────────────────────────────── */}
      {!skipCinema && (
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-x-0 bottom-[13dvh] flex flex-col items-center gap-3
            ${preReveal ? "lp-caption-in" : "lp-caption-out"}`}
        >
          {/* Tracking is Latin-only — spacing severs connected Arabic script. */}
          <span className={`text-xs text-ink-400 ${ar ? "" : "tracking-wide"}`}>
            {ar ? "جارٍ تجهيز مساحة العمل…" : "Preparing your workspace…"}
          </span>
          {/* The product's own loading signature — an indeterminate sweep,
              never a fake percentage. lp-sweep-bar resolves direction so the
              bar travels the reading direction under RTL too. */}
          <div className="relative h-px w-24 overflow-hidden bg-white/10">
            <div className="lp-sweep-bar absolute inset-y-0 start-0 w-1/3 bg-accent-500" />
          </div>
        </div>
      )}

      {/* ── Content column: lockup + card, in final layout from the start ──── */}
      <div className="relative z-10 mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center px-4 py-10">
        {/* The lockup occupies its final slot the whole time; the pre-settle
            transform floats it to optical centre for the reveal, so the settle
            beat is one transform returning to identity — no layout shift. */}
        <div
          className="flex flex-col items-center text-center"
          style={{
            opacity: preReveal && !skipCinema ? 0 : 1,
            filter: preReveal && !skipCinema ? "blur(16px)" : "blur(0px)",
            transform: settled ? "none" : "translateY(16dvh) scale(1.28)",
            transition: skipCinema
              ? "none"
              : [
                  "opacity 680ms ease-out",
                  "filter 760ms ease-out",
                  "transform 640ms cubic-bezier(0.16, 1, 0.3, 1)",
                ].join(", "),
          }}
        >
          {/* The reveal glow — the champagne halo the mark emerges inside. */}
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute -inset-10 rounded-full transition-opacity duration-700"
              style={{
                background:
                  "radial-gradient(circle, rgb(var(--accent-400) / 0.22), transparent 65%)",
                opacity: settled ? 0.55 : phase === "reveal" ? 1 : 0,
              }}
            />
            {branding.logoUrl ? (
              <BrandLogo size={64} className="relative max-h-16" />
            ) : (
              // Float and glow belong to the same ambient pass as the steam,
              // and stop with it.
              <LaundryMark
                size={64}
                className="relative"
                idleMotion={settled && ambient && !reduced}
                glow={ambient && !reduced}
              />
            )}
          </div>

          <div
            className={`mt-4 text-2xl font-semibold text-accent-300 ${latinName ? "lp-wordmark-latin uppercase" : ""}`}
          >
            {appName}
          </div>
          <div className="mt-1.5 text-sm text-ink-300">{tagline}</div>
        </div>

        {/* The card. Hidden from layout, readers and the tab order until it
            lands — `visibility` handles all three at once. */}
        <div
          className={`mt-8 w-full ${settled ? (skipCinema ? "animate-fade-in" : "lp-card-in") : "invisible opacity-0"}`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
